import React, { useMemo, useRef, useCallback, useState } from 'react';
import { StyleSheet, View, ScrollView, Platform, Keyboard } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useTranslation from '@/hooks/useTranslation';
import { useAuthStore, useUIStore } from '@/store';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import ChatInputBanners from '@/components/ChatInput/ChatInputBanners';
import ChatInputFilesContainer from '@/components/ChatInput/ChatInputFilesContainer';
import ChatInputQuoteContainer from '@/components/ChatInput/ChatInputQuoteContainer';
import ChatInputTextarea from '@/components/ChatInput/ChatInputTextarea';
import ChatInputModelsButton from '@/components/ChatInput/ChatInputModelsButton';
import ChatInputCreditsButton from '@/components/ChatInput/ChatInputCreditsButton';
import NumberControl from '@/components/ChatInput/CustomParameterOptions/NumberControl';
import ToggleControl from '@/components/ChatInput/CustomParameterOptions/ToggleControl';
import DateRangeControl from '@/components/ChatInput/CustomParameterOptions/DateRangeControl';
import TextareaControl from '@/components/ChatInput/CustomParameterOptions/TextareaControl';
import TextareaMultiControl from '@/components/ChatInput/CustomParameterOptions/TextareaMultiControl';
import ChatInputCustomParameterButton from '@/components/ChatInput/ChatInputCustomParameterButton';
import { isFileParam } from '@/components/ChatInput/fileParamUtils';
import FastModeButton from '@/components/ChatInput/FastModeButton';
import WorkThinkingPowerButton from '@/components/ChatInput/WorkThinkingPowerButton';
import {
  CHAT_INPUT_STYLE,
  MAX_MODELS_SELECTABLE,
  DEFAULT_CHAT_INPUT_DETAILED_WIDTH,
  DEFAULT_CHAT_INPUT_DETAILED_WIDTH_MIN,
  DEFAULT_CHAT_INPUT_DETAILED_WIDTH_MAX,
} from '@/constants/config';
import { useTooltipAnchor } from '@/hooks/useTooltipAnchor';

const createStyles = (colors) => StyleSheet.create({
  container: {
    width: '100%',
    height: '100%',
    borderRightWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_primary,
    overflow: 'hidden',
  },
  header: {
    height: 53,
    borderBottomWidth: 1,
    borderBottomColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_block,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 10,
  },
  headerTitle: {
    fontSize: 14,
  },
  inner: {
    flex: 1,
    height: '100%',
    flexDirection: 'column',
  },
  scrollArea: {
    maxHeight: '100%',
  },
  scrollContent: {
    gap: 10,
    paddingBottom: 15,
  },
  sectionHeader: {
    marginBottom: 4,
  },
  sectionHeaderStacked: {
    marginBottom: 10,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  sectionTitle: {
    fontSize: 13,
  },
  requiredBadge: {
    backgroundColor: 'rgba(255, 40, 40, 0.12)',
    color: 'rgb(255, 40, 40)',
    borderRadius: 6,
    paddingVertical: 2,
    paddingHorizontal: 6,
    fontSize: 10,
    textTransform: 'uppercase',
  },
  sectionDescription: {
    fontSize: 12,
    color: colors.text_color_tertiary,
    marginTop: 5,
  },
  parameterBlock: {
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  parameterBlockInline: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  inlineLabelColumn: {
    flex: 1,
    paddingRight: 5,
  },
  inlineControlColumn: {
    alignItems: 'flex-end',
  },
  filesQuoteSection: {
    gap: 10,
  },
  mainInputSection: {
    gap: 10,
    paddingTop: 5,
    paddingHorizontal: 10,
  },
  mainInputLabel: {
    fontSize: 13,
  },
  mainInputContainer: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_primary,
    padding: Platform.OS === 'web' ? 0 : 4,
    minHeight: 96,
  },
  modelsCreditsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    paddingHorizontal: 10,
  },
  sendSection: {
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
    paddingTop: 10,
    paddingBottom: 15,
  },
  sendButtonWrapper: {
    marginTop: 10,
    paddingHorizontal: 10,
  },
  missingRequiredParametersContainer: {
    paddingHorizontal: 10,
    paddingTop: 15,
  },
  resizeHandle: {
    position: 'absolute',
    top: 0,
    right: -5,
    width: 10,
    height: '100%',
    cursor: 'ew-resize',
    zIndex: 2,
  },
  resizeHandleActive: {
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
  animated: {
    transition: 'width 0.24s ease-in-out',
  },
});

/**
 * ChatInputDetailed
 *
 * Vertical "detailed" input panel:
 * - All parameters rendered in a stacked form with their full controls
 * - Main message textarea near the bottom
 * - Full-width Send button at the very bottom
 *
 * This component is designed to consume the same props shape that
 * ChatInputFixed / ChatInputFloating receive from `ChatInput.jsx`.
 */
export default function ChatInputDetailed({
  placeholder = 'Type a message...',
  value = '',
  credits,
  holdCredits,
  withCredits = true,
  banners,
  quote,

  // Core handlers
  onActionPress,
  onChangeText,
  onFocus,
  onBlur,
  onPasteFiles,

  // Status
  isInferencing = false,
  isLoading = false,
  isDisabled = false,
  isCancelable = false,
  autoFocus = false,
  isMissingRequiredParameters = false,
  highlightedMissingParameterIds = [],

  // Parameters
  parameters = [],
  onParameterChange,
  selectedParameters = {},

  // Files
  files = [],
  onAddFile,
  onRemoveFile,
  onSetFiles,

  // Models / agents
  selectedModelIds = [],
  selectedModels = [],
  onSetSelectedModels,
  selectedAgentIds = [],
  selectedAgents = [],
  onSetSelectedAgents,

  // Other
  onSetQuote,
  modelInputs,
  modelOutputs,
  conversationId,
  hideVoiceMode = false, // eslint-disable-line no-unused-vars
  hideTextInput = false,
  withModels = true,
  isWorkMode = false,
}) {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const modelSelectorAnchorRef = useTooltipAnchor('model-selector');
  const creditsBadgeAnchorRef = useTooltipAnchor('credits-badge');

  const storeWidth = useUIStore(state => state.chatInputDetailedWidth);
  const setStoreWidth = useUIStore(state => state.setChatInputDetailedWidth);
  const effectiveWidth = typeof storeWidth === 'number' ? storeWidth : DEFAULT_CHAT_INPUT_DETAILED_WIDTH;

  const [isResizing, setIsResizing] = useState(false);

  const handleResizeMouseDown = useCallback((e) => {
    if (Platform.OS !== 'web') return;
    e.preventDefault();
    setIsResizing(true);

    const rect = containerRef.current?.getBoundingClientRect
      ? containerRef.current.getBoundingClientRect()
      : { left: 0 };
    const left = rect.left || 0;

    let rafId;
    const handleMouseMove = (moveEvent) => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const rawWidth = moveEvent.clientX - left;
        const newWidth = Math.min(
          Math.max(rawWidth, DEFAULT_CHAT_INPUT_DETAILED_WIDTH_MIN),
          DEFAULT_CHAT_INPUT_DETAILED_WIDTH_MAX
        );
        setStoreWidth(newWidth);
      });
    };

    const handleMouseUp = (upEvent) => {
      setIsResizing(false);
      if (rafId) cancelAnimationFrame(rafId);
      const rawWidth = upEvent.clientX - left;
      const finalWidth = Math.min(
        Math.max(rawWidth, DEFAULT_CHAT_INPUT_DETAILED_WIDTH_MIN),
        DEFAULT_CHAT_INPUT_DETAILED_WIDTH_MAX
      );
      setStoreWidth(finalWidth);
      if (Platform.OS === 'web') {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      }
    };

    if (Platform.OS === 'web') {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
  }, [setStoreWidth]);

  const handleOnRemoveQuote = useCallback(() => {
    onSetQuote?.('');
  }, [onSetQuote]);

  // Wrapper to dismiss keyboard on native before calling onActionPress
  const handleSendPress = useCallback(() => {
    if (Platform.OS !== 'web') {
      Keyboard.dismiss();
    }
    onActionPress?.();
  }, [onActionPress]);

  const handleAddUploadingFile = useCallback((uploadingFiles) => {
    const filesToAdd = Array.isArray(uploadingFiles) ? uploadingFiles : [uploadingFiles];
    filesToAdd.forEach((file) => {
      // Use onAddFile instead of onSetFiles to avoid stale closure issues
      // when multiple files are uploaded simultaneously
      onAddFile?.(file);
    });
  }, [onAddFile]);

  const handleParameterValueChange = useCallback((paramOrId, nextValue) => {
    // Support both full param objects and plain id strings
    let key = null;
    if (typeof paramOrId === 'string') {
      key = paramOrId;
    } else if (paramOrId && typeof paramOrId === 'object') {
      key = paramOrId.name || paramOrId.id;
    }
    if (!key) return;
    onParameterChange?.(key, nextValue);
  }, [onParameterChange]);

  const renderParameterControl = useCallback((param) => {
    if (!param || !param.ui) return null;

    const componentType = param.ui.component;
    const paramWithValue = {
      ...param,
      value: selectedParameters?.[param.id] ?? param.value,
    };

    const handleChange = (next) => handleParameterValueChange(param, next);

    if (componentType === 'number' || componentType === 'slider') {
      return (
        <NumberControl
          param={paramWithValue}
          onChange={handleChange}
        />
      );
    }

    if (componentType === 'toggle') {
      return (
        <ToggleControl
          param={paramWithValue}
          onChange={handleChange}
        />
      );
    }

    if (componentType === 'textarea') {
      return (
        <TextareaControl
          param={paramWithValue}
          onChange={handleChange}
          withoutSpacing={true}
          isPrimary={true}
        />
      );
    }

    if (componentType === 'textarea-multi') {
      return (
        <TextareaMultiControl
          param={paramWithValue}
          onChange={handleChange}
          isPrimary={true}
          withoutHeaderText={true}
          noSpacing={true}
        />
      );
    }

    if (componentType === 'daterange') {
      return (
        <DateRangeControl
          param={paramWithValue}
          onChange={handleChange}
          useMenuItems={false}
          noSpacing={true}
          withoutHeaderText={true}
        />
      );
    }

    // For select/action/multiselect/files/tools/indicator and any other types,
    // fall back to the existing parameter button, which already handles
    // complex behaviors (files, tools, plus-gating, etc.).
    return (
      <ChatInputCustomParameterButton
        icon={param.ui.icon}
        text={param.ui.text || param.value}
        parameterData={paramWithValue}
        selectedParameters={selectedParameters}
        selectedAgents={selectedAgents}
        selectedModels={selectedModels}
        selectedAgentIds={selectedAgentIds}
        selectedModelIds={selectedModelIds}
        onChange={handleParameterValueChange}
        files={files}
        onAddFile={onAddFile}
        onAddUploadingFile={handleAddUploadingFile}
        isHighlighted={highlightedMissingParameterIds?.includes(param?.id || param?.name)}
      />
    );
  }, [
    files,
    handleAddUploadingFile,
    handleParameterValueChange,
    onAddFile,
    selectedAgentIds,
    selectedAgents,
    selectedModelIds,
    selectedModels,
    selectedParameters,
  ]);

  const mainButtonLabel = isInferencing && isCancelable ? t('chatInput.stop') : t('chatInput.send');

  return (
    <View
      ref={containerRef}
      style={[
        styles.container,
        !isResizing && styles.animated,
        Platform.OS === 'web' && {
          width: effectiveWidth,
          maxWidth: effectiveWidth,
        },
      ]}
    >
      <View style={styles.header}>
        <Text style={styles.headerTitle} size='s' weight='medium' color='primary'>
          {t('chatInput.messageControls')}
        </Text>
      </View>
      <View style={styles.inner}>
        {Platform.OS === 'web' && (
          <View
            style={[styles.resizeHandle, isResizing && styles.resizeHandleActive]}
            onMouseDown={handleResizeMouseDown}
          />
        )}
        {/* Banners at the very top */}
        <ChatInputBanners banners={banners} isDetached={true} />

        <ScrollView
          style={styles.scrollArea}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          {/* Files & quote preview */}
          <View style={styles.filesQuoteSection}>
            <ChatInputFilesContainer
              files={files}
              onDeleteFile={onRemoveFile}
              isDetached={true}
              parameters={parameters}
            />
          </View>

          {/* Parameters stacked, with inline layout for toggles & dropdowns */}
          {Array.isArray(parameters) && parameters.map((param) => {
            if (!param || !param.ui) return null;
            // File-typed params are surfaced as ghost cards in the attachment row above.
            if (isFileParam(param)) return null;
            const displayName = param.ui.displayName || param.ui.text || param.id;
            const description = param.ui.description || param.description;
            const isRequired = !!param.validation?.required;

            const componentType = param.ui.component;
            const isDropdownLike = componentType === 'select' || componentType === 'action' || componentType === 'multiselect' || componentType === 'indicator';
            // Inline only for toggles and non-file dropdown-style params
            const isInline = componentType === 'toggle' || isDropdownLike;

            return (
              <View
                key={`detailed-param-${param.id}`}
                style={[styles.parameterBlock, isInline && styles.parameterBlockInline]}
              >
                {isInline ? (
                  <>
                    <View style={styles.inlineLabelColumn}>
                      <View style={styles.sectionHeader}>
                        <View style={styles.sectionTitleRow}>
                          <Text style={styles.sectionTitle} size='xs' color='secondary'>
                            {displayName}
                          </Text>
                          {isRequired && (
                            <Text style={styles.requiredBadge}>
                              {t('chatInput.required')}
                            </Text>
                          )}
                        </View>
                        {description ? (
                          <Text style={styles.sectionDescription}>
                            {description}
                          </Text>
                        ) : null}
                      </View>
                    </View>
                    <View style={styles.inlineControlColumn}>
                      {renderParameterControl(param)}
                    </View>
                  </>
                ) : (
                  <>
                    <View style={styles.sectionHeaderStacked}>
                      <View style={styles.sectionTitleRow}>
                        <Text style={styles.sectionTitle} size='xs' color='secondary'>
                          {displayName}
                        </Text>
                        {isRequired && (
                          <Text style={styles.requiredBadge}>
                            {t('chatInput.required')}
                          </Text>
                        )}
                      </View>
                      {description ? (
                        <Text style={styles.sectionDescription}>
                          {description}
                        </Text>
                      ) : null}
                    </View>

                    {renderParameterControl(param)}
                  </>
                )}
              </View>
            );
          })}

          {/* Main prompt textarea — hidden for models that don't consume a
              user prompt (transcription, upscalers, image-to-3D). */}
          {!hideTextInput && (
            <View style={styles.mainInputSection}>
              <Text style={styles.mainInputLabel} size='xs' color='secondary'>
                {t('chatInput.prompt')}
              </Text>
              <View style={styles.mainInputContainer}>
                <ChatInputTextarea
                  value={value}
                  onChangeText={onChangeText}
                  placeholder={placeholder}
                  inputRef={inputRef}
                  onKeyDown={undefined}
                  onPasteFiles={onPasteFiles}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  colors={colors}
                  theme={theme}
                  onContentSizeChange={undefined}
                  selectedAgents={selectedAgents}
                  onSetSelectedAgents={onSetSelectedAgents}
                  chatInputStyle={CHAT_INPUT_STYLE.FIXED}
                  autoFocus={autoFocus}
                />
              </View>
              <ChatInputQuoteContainer
                quote={quote}
                onRemoveQuote={handleOnRemoveQuote}
                isDetached={true}
              />
            </View>
          )}
        </ScrollView>

        {/* Models / credits + Send footer */}
        <View style={styles.sendSection}>
          <View style={styles.modelsCreditsRow}>
            {withModels && (
            <View ref={(node) => { modelSelectorAnchorRef.current = node; }}>
              <ChatInputModelsButton
                isDisabled={isInferencing}
                onChange={onSetSelectedModels}
                modelIds={selectedModelIds}
                shouldCloseOnSelect={true}
                maxModels={MAX_MODELS_SELECTABLE}
                inputs={modelInputs || (selectedParameters?.action === 'create-image' ? ['text'] : [])}
                outputs={modelOutputs || (selectedParameters?.action === 'create-image' ? ['image'] : [])}
                selectedModels={selectedModels}
                dropdownSide={conversationId && conversationId !== 'new' ? 'top' : 'bottom'}
              />
            </View>
            )}

            {withCredits && (
              <View ref={(node) => { creditsBadgeAnchorRef.current = node; }}>
                <ChatInputCreditsButton requestCost={credits} holdCost={holdCredits} />
              </View>
            )}
            
            {isWorkMode ? (
              <WorkThinkingPowerButton conversationId={conversationId} />
            ) : (
              <FastModeButton
                isDisabled={isInferencing}
                conversationId={conversationId}
              />
            )}
          </View>

          <View style={styles.sendButtonWrapper}>
            <Button
              text={mainButtonLabel}
              variant={isDisabled ? "secondary" : "primary"}
              size="large"
              isFullWidth
              onPress={handleSendPress}
              isDisabled={isDisabled}
              isLoading={isLoading}
              useImmediatePress
            />
          </View>

          {isMissingRequiredParameters && (
            <View style={styles.missingRequiredParametersContainer}>
              <Text size='xs' color='danger'>
                {t('chatInput.fillRequiredBeforeSend')}
              </Text>
            </View>
          )}
        </View>
      </View>
    </View>
  );
}


